import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LayoutGrid, List, LogIn, LogOut, Search, Settings, UserPlus } from 'lucide-react';
import type { Site, TodayBoard, TodayBoardRow } from '@cloudsign/shared';
import { t } from '../../lib/i18n';
import { useCan, useCaps } from '../../lib/session';
import { useSite } from '../../lib/site';
import { useLiveChannel } from '../../lib/realtime';
import { Alert, Badge, Button, EmptyRow, EmptyState, SortHeader, Spinner, TableWrap, Th } from '../../components/ui';
import { ErrorPanel } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { applyLive, boardKey, fetchBoard, isIn } from './api';
import { formatTime } from './format';
import { AnnotationBadges, FlagBadges, MethodIcon, PersonAvatar } from './parts';
import { SignInDialog } from './SignInDialog';
import { SignOutAllDialog, SignOutDialog } from './SignOutDialogs';
import { VisitDrawer } from './VisitDrawer';
import { CreateDialog } from '../preregistration/CreateDialog';
import { useActiveGroups } from '../preregistration/api';

type Layout = 'list' | 'cards';
type SortField = 'name' | 'group' | 'host' | 'in' | 'out';
const LAYOUT_KEY = 'cloudsign.today.layout';

function readLayout(): Layout {
  try {
    return window.localStorage.getItem(LAYOUT_KEY) === 'cards' ? 'cards' : 'list';
  } catch {
    return 'list';
  }
}

function writeLayout(l: Layout): void {
  try {
    window.localStorage.setItem(LAYOUT_KEY, l);
  } catch {
    // Blocked storage only means the choice is forgotten on reload.
  }
}

function searchText(r: TodayBoardRow, group: string): string {
  return [r.name, r.hostName ?? '', group, ...r.starred.map((s) => s.text)].join(' ').toLowerCase();
}

function StatTile({ label, value, active, onClick }: { label: string; value: number; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-xl border bg-white p-4 text-left shadow-sm transition-colors hover:border-brand ${active ? 'border-brand ring-1 ring-brand' : 'border-slate-200'}`}
    >
      <span className="block text-3xl font-bold tracking-tight text-slate-900">{value}</span>
      <span className="mt-1 block truncate text-sm text-slate-600">{label}</span>
    </button>
  );
}

/** Opens the pre-registration form for the selected site, with the selected group (if any) chosen first. */
function PreRegisterLauncher({ site, groupId, onClose }: { site: Site; groupId: string | null; onClose: () => void }) {
  const groups = useActiveGroups();
  if (!groups.data) return null;
  const ordered = groupId ? [...groups.data.filter((g) => g.id === groupId), ...groups.data.filter((g) => g.id !== groupId)] : groups.data;
  return <CreateDialog sites={[site]} groups={ordered} onClose={onClose} />;
}

export function TodayPage() {
  const caps = useCaps();
  const { sites, site, siteId, setSiteId, loading: siteLoading } = useSite();
  const { id: openId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const canSignInOut = useCan('today.sign_in_out');
  const canSignOutAll = useCan('today.sign_out_all');
  const moduleOn = (key: string) => caps.modules.some((m) => m.key === key && m.enabled);
  const canViewPre = useCan('preregistrations.view');
  const canPreRegister = canSignInOut && canViewPre && moduleOn('pre-registration');
  const canManageGroup = useCan('groups.manage') && moduleOn('groups');

  const [tab, setTab] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [show, setShow] = useState<'in' | 'all'>('in');
  const [layout, setLayoutState] = useState<Layout>(readLayout);
  const [sort, setSort] = useState<{ field: SortField; order: 'asc' | 'desc' }>({ field: 'in', order: 'desc' });
  const [dialog, setDialog] = useState<'signin' | 'signoutall' | 'prereg' | null>(null);
  const [signOut, setSignOut] = useState<TodayBoardRow | null>(null);

  const q = useQuery({
    queryKey: boardKey(siteId),
    queryFn: () => fetchBoard(siteId as string),
    enabled: Boolean(siteId),
  });

  const refetch = () => void qc.invalidateQueries({ queryKey: boardKey(siteId) });
  const { connected } = useLiveChannel(
    'today',
    siteId,
    (data) => {
      const current = qc.getQueryData<TodayBoard>(boardKey(siteId));
      const next = current ? applyLive(current, data) : null;
      if (next) qc.setQueryData(boardKey(siteId), next);
      else refetch();
    },
    refetch,
  );

  const board = q.data;
  const groupName = useMemo(() => new Map((board?.groups ?? []).map((g) => [g.groupId, g.name])), [board]);
  const rows = board?.rows ?? [];
  const inNowBy = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows) if (isIn(r)) m.set(r.groupId, (m.get(r.groupId) ?? 0) + 1);
    return m;
  }, [rows]);
  const totalIn = rows.filter(isIn).length;

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = rows.filter(
      (r) =>
        (tab === 'all' || r.groupId === tab) &&
        (show === 'all' || isIn(r)) &&
        (!term || searchText(r, groupName.get(r.groupId) ?? '').includes(term)),
    );
    const key = (r: TodayBoardRow): string => {
      switch (sort.field) {
        case 'name':
          return r.name.toLowerCase();
        case 'group':
          return (groupName.get(r.groupId) ?? '').toLowerCase();
        case 'host':
          return (r.hostName ?? '').toLowerCase();
        case 'out':
          return r.signedOutAt ?? '9999';
        default:
          return r.signedInAt;
      }
    };
    return [...list].sort((a, b) => (key(a) < key(b) ? -1 : key(a) > key(b) ? 1 : 0) * (sort.order === 'asc' ? 1 : -1));
  }, [rows, tab, show, search, sort, groupName]);

  const setLayout = (l: Layout) => {
    setLayoutState(l);
    writeLayout(l);
  };
  const toggleSort = (field: string) =>
    setSort((s) => ({ field: field as SortField, order: s.field === field && s.order === 'asc' ? 'desc' : 'asc' }));

  const tz = board?.timeZone ?? site?.timeZone ?? 'Europe/London';
  const firstName = caps.account.fullName.trim().split(/\s+/)[0] ?? '';
  const groups = board?.groups ?? [];

  const onDone = (text: string) => {
    toast(text);
    refetch();
  };

  if (siteLoading) return <Spinner />;
  if (!site || !siteId) return <EmptyState message={t('today.noSite')} />;

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">
          {t('today.greeting', { name: firstName })}{' '}
          <label className="inline-block">
            <span className="sr-only">{t('site.picker')}</span>
            <select
              value={siteId}
              onChange={(e) => setSiteId(e.target.value)}
              className="rounded-md border border-transparent bg-transparent py-0.5 pl-1 pr-6 text-2xl font-bold text-brand hover:border-slate-300"
            >
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
        </h1>
        <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${connected ? 'bg-green-100 text-green-900' : 'bg-amber-100 text-amber-900'}`}>
          <span aria-hidden="true" className={`h-2 w-2 rounded-full ${connected ? 'bg-green-600' : 'bg-amber-600'}`} />
          {connected ? t('today.live') : t('today.reconnecting')}
        </span>
      </div>

      {q.isError ? <ErrorPanel error={q.error} onRetry={() => void q.refetch()} /> : null}
      {board?.truncated ? <div className="mb-3"><Alert kind="info">{t('today.truncated')}</Alert></div> : null}

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" role="group" aria-label={t('today.tiles')}>
        <StatTile label={t('today.tab.everyone')} value={totalIn} active={tab === 'all'} onClick={() => setTab('all')} />
        {groups.map((g) => (
          <StatTile key={g.groupId} label={g.name} value={inNowBy.get(g.groupId) ?? 0} active={tab === g.groupId} onClick={() => setTab(g.groupId)} />
        ))}
      </div>

      <div role="tablist" aria-label={t('today.tabs')} className="mb-4 flex gap-1 overflow-x-auto border-b border-slate-200">
        {[{ id: 'all', name: t('today.tab.everyone'), n: totalIn }, ...groups.map((g) => ({ id: g.groupId, name: g.name, n: inNowBy.get(g.groupId) ?? 0 }))].map((x) => (
          <button
            key={x.id}
            type="button"
            role="tab"
            aria-selected={tab === x.id}
            onClick={() => setTab(x.id)}
            className={`-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium ${tab === x.id ? 'border-brand text-brand' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
          >
            {t('today.tab.label', { name: x.name, count: x.n })}
          </button>
        ))}
      </div>

      <div role="group" aria-label={t('common.filters')} className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <div className="relative min-w-48 flex-1">
          <Search aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <label className="sr-only" htmlFor="today-search">{t('today.search')}</label>
          <input
            id="today-search"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('today.search')}
            className="block min-h-10 w-full rounded-md border border-slate-300 bg-white py-1.5 pl-9 pr-3 text-sm sm:min-h-9"
          />
        </div>
        <div role="group" aria-label={t('today.show')} className="inline-flex overflow-hidden rounded-md border border-slate-300">
          {(['in', 'all'] as const).map((s) => (
            <button
              key={s}
              type="button"
              aria-pressed={show === s}
              onClick={() => setShow(s)}
              className={`px-3 py-1.5 text-sm ${show === s ? 'bg-brand-soft font-medium text-slate-900' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
            >
              {s === 'in' ? t('today.show.in') : t('today.show.all')}
            </button>
          ))}
        </div>
        <div role="group" aria-label={t('today.layout')} className="inline-flex overflow-hidden rounded-md border border-slate-300">
          <button type="button" aria-pressed={layout === 'list'} aria-label={t('today.layout.list')} title={t('today.layout.list')} onClick={() => setLayout('list')} className={`p-2 ${layout === 'list' ? 'bg-brand-soft' : 'bg-white hover:bg-slate-50'}`}>
            <List aria-hidden="true" className="h-4 w-4" />
          </button>
          <button type="button" aria-pressed={layout === 'cards'} aria-label={t('today.layout.cards')} title={t('today.layout.cards')} onClick={() => setLayout('cards')} className={`p-2 ${layout === 'cards' ? 'bg-brand-soft' : 'bg-white hover:bg-slate-50'}`}>
            <LayoutGrid aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>
        {canSignOutAll ? (
          <Button disabled={totalIn === 0} onClick={() => setDialog('signoutall')}>
            <LogOut aria-hidden="true" className="h-4 w-4" />
            {t('today.signOutAll.button')}
          </Button>
        ) : null}
        {canManageGroup && tab !== 'all' ? (
          <Link
            to={`/manage/groups/${tab}`}
            aria-label={t('today.manageGroup.for', { name: groupName.get(tab) ?? '' })}
            className="inline-flex min-h-10 items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-3.5 py-1.5 text-sm font-medium text-slate-800 hover:bg-slate-50 sm:min-h-9"
          >
            <Settings aria-hidden="true" className="h-4 w-4" />
            {t('today.manageGroup.button')}
          </Link>
        ) : null}
        {canPreRegister ? (
          <Button onClick={() => setDialog('prereg')}>
            <UserPlus aria-hidden="true" className="h-4 w-4" />
            {t('today.preregister.button')}
          </Button>
        ) : null}
        {canSignInOut ? (
          <Button variant="primary" disabled={groups.length === 0} onClick={() => setDialog('signin')}>
            <LogIn aria-hidden="true" className="h-4 w-4" />
            {t('today.signIn.button')}
          </Button>
        ) : null}
      </div>

      {q.isPending ? <Spinner /> : null}

      {board && layout === 'list' ? (
        <TableWrap caption={t('today.caption')}>
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              <SortHeader label={t('today.col.name')} field="name" sort={sort.field} order={sort.order} onSort={toggleSort} />
              <SortHeader label={t('today.col.group')} field="group" sort={sort.field} order={sort.order} onSort={toggleSort} />
              <SortHeader label={t('today.col.host')} field="host" sort={sort.field} order={sort.order} onSort={toggleSort} />
              <Th>{t('today.col.details')}</Th>
              <SortHeader label={t('today.col.in')} field="in" sort={sort.field} order={sort.order} onSort={toggleSort} />
              <SortHeader label={t('today.col.out')} field="out" sort={sort.field} order={sort.order} onSort={toggleSort} />
              <Th>
                <span className="sr-only">{t('today.col.actions')}</span>
              </Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {visible.length === 0 ? <EmptyRow cols={7} action={undefined} /> : null}
            {visible.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50">
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-3">
                    <PersonAvatar name={r.name} fileId={r.photoThumbFileId} />
                    <Link to={`/today/visits/${r.id}`} className="font-medium text-slate-900 hover:text-brand hover:underline">
                      {r.name}
                    </Link>
                    <FlagBadges flags={r.flags} />
                    <AnnotationBadges annotations={r.annotations} />
                  </div>
                </td>
                <td className="px-4 py-2.5 text-slate-700">{groupName.get(r.groupId)}</td>
                <td className="px-4 py-2.5 text-slate-700">{r.hostName}</td>
                <td className="px-4 py-2.5 text-slate-700">{r.starred.map((s) => s.text).join(' · ')}</td>
                <td className="px-4 py-2.5 text-slate-700">
                  <span className="inline-flex items-center gap-2">
                    {formatTime(r.signedInAt, tz)}
                    <MethodIcon method={r.signInMethod} device={r.signInDeviceName} />
                  </span>
                </td>
                <td className="px-4 py-2.5 text-slate-700">{r.signedOutAt ? formatTime(r.signedOutAt, tz) : <Badge tone="good">{t('today.status.in')}</Badge>}</td>
                <td className="px-4 py-2.5 text-right">
                  {canSignInOut && isIn(r) ? (
                    <Button onClick={() => setSignOut(r)} aria-label={t('today.signOut.for', { name: r.name })}>
                      {t('today.signOut.button')}
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      ) : null}

      {board && layout === 'cards' ? (
        visible.length === 0 ? (
          <EmptyState />
        ) : (
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((r) => (
              <li key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-start gap-3">
                  <PersonAvatar name={r.name} fileId={r.photoThumbFileId} size="h-12 w-12 text-sm" />
                  <div className="min-w-0 flex-1">
                    <Link to={`/today/visits/${r.id}`} className="block truncate font-semibold text-slate-900 hover:text-brand hover:underline">
                      {r.name}
                    </Link>
                    <p className="truncate text-sm text-slate-600">{groupName.get(r.groupId)}</p>
                    {r.hostName ? <p className="truncate text-sm text-slate-600">{t('today.card.host', { name: r.hostName })}</p> : null}
                  </div>
                  <MethodIcon method={r.signInMethod} device={r.signInDeviceName} />
                </div>
                {r.starred.length > 0 ? <p className="mt-2 text-sm text-slate-700">{r.starred.map((s) => s.text).join(' · ')}</p> : null}
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm">
                  <span className="text-slate-700">
                    {t('today.card.times', { from: formatTime(r.signedInAt, tz), to: r.signedOutAt ? formatTime(r.signedOutAt, tz) : t('today.status.in') })}
                  </span>
                  <span className="flex items-center gap-2">
                    <FlagBadges flags={r.flags} />
                    <AnnotationBadges annotations={r.annotations} />
                    {canSignInOut && isIn(r) ? (
                      <Button onClick={() => setSignOut(r)} aria-label={t('today.signOut.for', { name: r.name })}>
                        {t('today.signOut.button')}
                      </Button>
                    ) : null}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )
      ) : null}

      {dialog === 'signin' ? (
        <SignInDialog
          siteId={siteId}
          groups={groups}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            onDone(t('today.signIn.done'));
          }}
        />
      ) : null}
      {dialog === 'prereg' ? <PreRegisterLauncher site={site} groupId={tab === 'all' ? null : tab} onClose={() => setDialog(null)} /> : null}
      {dialog === 'signoutall' ? (
        <SignOutAllDialog
          siteId={siteId}
          groups={groups}
          onClose={() => setDialog(null)}
          onDone={(n) => {
            setDialog(null);
            onDone(t('today.signOutAll.done', { count: n }));
          }}
        />
      ) : null}
      {signOut ? (
        <SignOutDialog
          visitId={signOut.id}
          name={signOut.name}
          timeZone={tz}
          onClose={() => setSignOut(null)}
          onDone={() => {
            setSignOut(null);
            onDone(t('today.signOut.done'));
          }}
        />
      ) : null}
      {openId ? <VisitDrawer visitId={openId} onClose={() => navigate('/today')} onChanged={refetch} /> : null}
    </div>
  );
}
